import { without } from "../fields";
import { type AxialCoord, hexDistance, hexEquals, hexNeighbors, hexRing } from "../hex";
import { isWorkingCityOf, workingCities } from "../land/cities";
import { nearestTo, rankBy } from "../pick";
import { enlistedWith } from "../soldier/company";
import { bornHp, fallenSeat } from "../soldier/valor";
import type { RunOptions, RunRules, SoldierRules, SoldierSeatRules } from "../types/rules";
import type {
  FactionId,
  HexState,
  Role,
  RunState,
  SeatId,
  SeatRole,
  SeatState,
  Unit,
} from "../types/state";
import { spawnHexFor } from "../units/roster";
import { addUnit, newUnit } from "../units/unit";
import { enemyWithin, findHex, unitAt, unitById, unitsOf } from "./board";
import { kingOf } from "./pools";

/**
 * Who holds what (ADR-0014, ADR-0017 K0): the table of seats, a King's or a soldier's, each held
 * by a human or played by a script; the kingdoms the scripts play; a soldier seat's unit, its
 * spawn and respawn. With the door and the scripts this is the only module that reads `held`
 * (`test/boundaries.test.ts`): every other rule pays, spawns and counts a seat the same held or not.
 */

/** A seat's place in the table before the run exists: its kingdom and role; its number is its index. */
export interface SeatPlace {
  readonly faction: FactionId;
  readonly role: SeatRole;
}

/**
 * The soldier seats a kingdom has: with K1's seats every kingdom the same (parity, ADR-0017),
 * else K0's one in the first kingdom of a soldier's run (M1), none elsewhere.
 */
export function soldierSeatsOf(rules: RunRules, kingdomIndex: number): number {
  if (!rules.soldier) {
    return 0;
  }
  return rules.soldier.seats?.perKingdom ?? (kingdomIndex === 0 ? 1 : 0);
}

/**
 * The run's seats by formula (K0, ADR-0017 point 1): kingdom-major in the run's order, each
 * kingdom's King and then its soldier seats, so a seat's number is fixed by the mode. The room
 * builds the same table without the core (`seatTable`, `src/net/protocol.ts`), a test proves it.
 */
export function seatPlaces(factions: readonly FactionId[], rules: RunRules): SeatPlace[] {
  return factions.flatMap((faction, index): SeatPlace[] => [
    { faction, role: "king" },
    ...Array.from(
      { length: soldierSeatsOf(rules, index) },
      (): SeatPlace => ({
        faction,
        role: "soldier",
      }),
    ),
  ]);
}

/** The seats a run starts held when nobody says (B3.3, M1): the first kingdom's soldier seat, else its King's. */
export function defaultHeld(places: readonly SeatPlace[]): SeatId[] {
  return [places[1]?.role === "soldier" ? 1 : 0];
}

/**
 * A new run's seats (K0): the table (`seatPlaces`), held as the options say (else
 * `defaultHeld`), each soldier seat with its ledger at nothing, none of its units spawned yet.
 */
export function newSeats(factions: readonly FactionId[], options: RunOptions): SeatState[] {
  const { soldier, finds, bounty } = options.rules;
  const places = seatPlaces(factions, options.rules);
  const held = options.held ?? defaultHeld(places);
  return places.map(
    ({ faction, role }, id): SeatState => ({
      id,
      faction,
      role,
      held: held.includes(id),
      // VC: every run starts with nothing in hand; the account's coins and renown stay in the save.
      ...(role === "soldier" && (finds || bounty) ? { purse: 0 } : {}),
      ...(role === "soldier" && bounty ? { renown: 0, owed: 0 } : {}),
      ...(role === "soldier" && soldier?.valor ? { valor: 0, peakValor: 0, peakTick: 0 } : {}),
    }),
  );
}

/** The seat with this number; undefined past the table. */
export function seatAt(state: RunState, id: SeatId): SeatState | undefined {
  return state.seats[id];
}

/** The run with one seat's record replaced, every other seat and key in its place. */
export function withSeat(state: RunState, seat: SeatState): RunState {
  return { ...state, seats: state.seats.map((each) => (each.id === seat.id ? seat : each)) };
}

/** The soldier seats, in id order: every step that touches one goes through them in this order. */
export function soldierSeats(state: RunState): SeatState[] {
  return state.seats.filter((seat) => seat.role === "soldier");
}

/** The number of a kingdom's King's seat; undefined for a kingdom not in the run. */
export function kingSeatOf(state: RunState, faction: FactionId): SeatId | undefined {
  return state.seats.find((seat) => seat.faction === faction && seat.role === "king")?.id;
}

/**
 * Who holds the seats from here on (B3.7, K0): every seat named is held, every other is its
 * script's. A turn of the room carries it, so every page makes the change at the same tick. Same
 * object back when nothing changes.
 */
export function withHeld(state: RunState, held: readonly SeatId[]): RunState {
  let changed = false;
  const seats = state.seats.map((seat) => {
    const holds = held.includes(seat.id);
    if (seat.held === holds) {
      return seat;
    }
    changed = true;
    return { ...seat, held: holds };
  });
  return changed ? { ...state, seats } : state;
}

/** The seats humans hold, in id order. */
export function heldSeats(state: RunState): SeatId[] {
  return state.seats.filter((seat) => seat.held).map((seat) => seat.id);
}

/** What of a soldier seat outlives its unit (M2): everything but the unit and the tick it fell. */
function keptBySeat(seat: SeatState): SeatState {
  return without(seat, "unitId", "diedTick");
}

/**
 * Where a soldier seat's unit arrives (M1): at the gate, not in the yard. The yard (the city hex)
 * is where the King's recruits appear and a unit standing there holds them back, so the
 * soldier takes the nearest free own hex beside the city, else a free, open, unrazed neutral
 * neighbour of it (at tick 0 the kingdom owns one hex), else the yard itself, else waits.
 */
function seatSpawnHexFor(state: RunState, city: HexState): AxialCoord | undefined {
  const own = spawnHexFor(state, city);
  if (own && !hexEquals(own, city.coord)) {
    return own;
  }
  const gate = hexNeighbors(city.coord).find((coord) => {
    const hex = findHex(state, coord);
    return (
      hex !== undefined &&
      hex.owner === "none" &&
      !hex.razed &&
      hex.walls === 0 &&
      unitAt(state, coord) === undefined
    );
  });
  return gate ?? own;
}

/** M1: the gate of the capital, else of the first working city; C2: of the city nearest the seat's commander. */
function gateHexOf(state: RunState, seat: SeatState): AxialCoord | undefined {
  const king = kingOf(state, seat.faction);
  const cities = workingCities(state, seat.faction);
  const leader = enlistedWith(state, seat);
  const home = leader
    ? nearestTo(cities, leader.coord)
    : ((king && cities.find((city) => hexEquals(city.coord, king.capital))) ?? cities[0]);
  return home && seatSpawnHexFor(state, home);
}

/** K1: no unit of another kingdom stands within `safeRange` of the city. */
export function isSafeCity(state: RunState, city: HexState, seats: SoldierSeatRules): boolean {
  return !enemyWithin(state, city.owner, city.coord, seats.safeRange);
}

/**
 * K1 (section 7.2): the own working city a seat's next life rises by: the one it chose
 * (`deploy`), else the capital; one lost since is read as the own working city nearest it, and
 * C2's commander comes first as at the gate. From there the nearest safe one (`isSafeCity`), or,
 * none being safe, the chosen one all the same: a besieged last city still gets its defenders.
 * Undefined while the kingdom has no working city.
 */
export function riseCityOf(
  state: RunState,
  seat: SeatState,
  seats: SoldierSeatRules,
): HexState | undefined {
  const cities = workingCities(state, seat.faction);
  const wanted =
    enlistedWith(state, seat)?.coord ?? seat.deploy ?? kingOf(state, seat.faction)?.capital;
  const chosen =
    wanted === undefined
      ? cities[0]
      : (cities.find((city) => hexEquals(city.coord, wanted)) ?? nearestTo(cities, wanted));
  if (!chosen || isSafeCity(state, chosen, seats)) {
    return chosen;
  }
  return nearestTo(cities, chosen.coord, (city) => isSafeCity(state, city, seats)) ?? chosen;
}

/**
 * K1: the first free open hex on the rings round the city, nearest ring first and in direction
 * order: on the map, unrazed, wall-free, the kingdom's or nobody's, no unit on it. The yard (the
 * city itself, where its recruits appear) only once every ring hex is taken; undefined when
 * that is taken too, and the seat waits for the next wave.
 */
function ringHexFor(
  state: RunState,
  city: HexState,
  seats: SoldierSeatRules,
): AxialCoord | undefined {
  for (let radius = 1; radius <= seats.ringRadius; radius += 1) {
    const free = hexRing(city.coord, radius).find((coord) => {
      const hex = findHex(state, coord);
      return (
        hex !== undefined &&
        !hex.razed &&
        hex.walls === 0 &&
        (hex.owner === city.owner || hex.owner === "none") &&
        unitAt(state, coord) === undefined
      );
    });
    if (free) {
      return free;
    }
  }
  return unitAt(state, city.coord) === undefined ? city.coord : undefined;
}

/**
 * K1: where a seat's next life stands: the rings of the city it rises by (`riseCityOf`); that
 * city full, the next own working city with room, the safe ones first, each nearest it first. A
 * seat waits only while no city of its kingdom has room.
 */
function risingHexOf(
  state: RunState,
  seat: SeatState,
  seats: SoldierSeatRules,
): AxialCoord | undefined {
  const city = riseCityOf(state, seat, seats);
  if (!city) {
    return undefined;
  }
  const others = rankBy(
    workingCities(state, seat.faction).filter((other) => !hexEquals(other.coord, city.coord)),
    (other) => (isSafeCity(state, other, seats) ? 0 : 1),
    (other) => hexDistance(other.coord, city.coord),
  );
  for (const each of [city, ...others]) {
    const at = ringHexFor(state, each, seats);
    if (at) {
      return at;
    }
  }
  return undefined;
}

/**
 * A soldier seat's unit arrives as the weakest soldier of its kingdom (M1): at the capital's gate
 * (`gateHexOf`), or with K1's seats on the rings of the city it rises by (`risingHexOf`), for no
 * supply and no recruit (the account's, not the pool's), outside the army cap. A kingdom with no
 * city to stand by leaves the seat empty until one stands again. Every unit is born a seat's only
 * through here.
 */
export function spawnSeat(state: RunState, id: SeatId, soldier: SoldierRules): RunState {
  const seat = seatAt(state, id);
  if (!seat) {
    return state;
  }
  const at = soldier.seats ? risingHexOf(state, seat, soldier.seats) : gateHexOf(state, seat);
  if (!at) {
    return state;
  }
  // V1: it comes back with its title's HP.
  const unit = {
    ...newUnit(state, soldier.kind, seat.faction, at),
    hp: bornHp(state, seat, soldier.kind),
  };
  return withSeat(addUnit(state, unit), { ...keptBySeat(seat), unitId: unit.id });
}

/** The living unit a soldier seat plays; undefined for a King's seat and while the soldier is dead. */
export function seatUnit(state: RunState, seat: SeatState | undefined): Unit | undefined {
  return seat?.unitId === undefined ? undefined : unitById(state, seat.unitId);
}

/** The soldier seat this unit is the unit of, held or not; undefined for every other unit. */
export function seatOfUnit(state: RunState, unitId: number): SeatState | undefined {
  for (const seat of state.seats) {
    if (seat.unitId === unitId) {
      return seat;
    }
  }
  return undefined;
}

/** Whether this unit is a soldier seat's (M1), held or not: its title, ledger and work are its seat's. */
export function isSeatUnit(state: RunState, unitId: number): boolean {
  return seatOfUnit(state, unitId) !== undefined;
}

/**
 * Whether this unit is a held seat's (M1, D8): no seat but its own orders it, no script gathers,
 * promotes, shoves or sends it to govern.
 */
export function isHeldUnit(state: RunState, unitId: number): boolean {
  return seatOfUnit(state, unitId)?.held === true;
}

/**
 * A soldier seat's own unit when a command about it is its own word (D8: where it walks and what
 * it carries are its own): the run runs, the unit is a seat's and the faction's, and the issuer is
 * the soldier. Undefined otherwise; the soldier's verbs (sell, enlist) ask it first.
 */
export function soldiersOwnUnit(
  state: RunState,
  faction: FactionId,
  unitId: number,
  issuer: Role,
): Unit | undefined {
  const unit = unitById(state, unitId);
  return state.status === "running" &&
    unit !== undefined &&
    unit.owner === faction &&
    issuer === "soldier" &&
    isSeatUnit(state, unitId)
    ? unit
    : undefined;
}

/** A faction's units as its scripts see them: every one but a held seat's (D8 of the sketch). */
export function scriptUnitsOf(state: RunState, faction: FactionId): Unit[] {
  const units = unitsOf(state, faction);
  const held = state.seats.filter((seat) => seat.held && seat.unitId !== undefined);
  return held.length === 0
    ? units
    : units.filter((unit) => !held.some((seat) => seat.unitId === unit.id));
}

/** The kingdoms the scripts play: every one whose King's seat no human holds. */
export function scriptedFactions(state: RunState): FactionId[] {
  const held = humanKings(state);
  return state.factions.filter((faction) => !held.includes(faction));
}

const HUMAN_KINGS = new WeakMap<readonly SeatState[], readonly FactionId[]>();

/**
 * The kingdoms whose King a human holds (B3.3), in seat order: in a King's run the player's, in
 * a soldier's run none (M1), whose human is one unit and whose King is a script.
 */
export function humanKings(state: RunState): readonly FactionId[] {
  const cached = HUMAN_KINGS.get(state.seats);
  if (cached) {
    return cached;
  }
  const kings = state.seats
    .filter((seat) => seat.role === "king" && seat.held)
    .map((seat) => seat.faction);
  HUMAN_KINGS.set(state.seats, kings);
  return kings;
}

/**
 * Step 1d (M1): a soldier seat's dead unit comes back, seat by seat in id order. The tick it is
 * found gone is kept, and the seat loses its share of valor then (V1); after
 * `soldier.respawnTicks` more the seat spawns again as at the start of the run, and waits, tick
 * by tick, while no own city has a free hex to put it on. With K1's seats it rises only on its
 * kingdom's wave (`isWaveTick`), and a seat with nowhere to stand waits for the next one.
 */
export function respawnSeats(state: RunState): RunState {
  const soldier = state.rules.soldier;
  if (!soldier) {
    return state;
  }
  let next = state;
  for (const seat of soldierSeats(state)) {
    next = respawnSeat(next, seat.id, soldier);
  }
  return next;
}

function respawnSeat(state: RunState, id: SeatId, soldier: SoldierRules): RunState {
  const seat = seatAt(state, id);
  if (!seat || seatUnit(state, seat)) {
    return state;
  }
  if (seat.diedTick === undefined) {
    const fallen = fallenSeat(state.rules, keptBySeat(seat));
    return withSeat(state, { ...fallen, diedTick: state.tick });
  }
  if (
    state.tick - seat.diedTick < soldier.respawnTicks ||
    (soldier.seats && !isWaveTick(state, seat.faction, soldier.seats))
  ) {
    return state;
  }
  const spawned = spawnSeat(withSeat(state, keptBySeat(seat)), id, soldier);
  return seatUnit(spawned, seatAt(spawned, id)) ? spawned : state;
}

/**
 * K1: the kingdom's wave comes this tick: kingdom i's on the ticks where tick % `waveTicks` is
 * i % `waveTicks`, so the dead of a kingdom rise together and no kingdom always rises first (the
 * scripts' stagger, ADR-0012).
 */
export function isWaveTick(state: RunState, faction: FactionId, seats: SoldierSeatRules): boolean {
  const index = state.factions.indexOf(faction);
  return index >= 0 && state.tick % seats.waveTicks === index % seats.waveTicks;
}

/**
 * K1, the seat's `deploy` command: its next life rises by this own working city (`riseCityOf`),
 * dead or alive. Only a soldier seat of K1's seats, while the run runs; same object back when
 * refused or when it names the city already chosen.
 */
export function deploySeat(state: RunState, id: SeatId, city: AxialCoord): RunState {
  const seat = seatAt(state, id);
  if (
    state.status !== "running" ||
    !state.rules.soldier?.seats ||
    seat?.role !== "soldier" ||
    !isWorkingCityOf(findHex(state, city), seat.faction, state.tick) ||
    (seat.deploy !== undefined && hexEquals(seat.deploy, city))
  ) {
    return state;
  }
  return withSeat(state, { ...seat, deploy: city });
}

/**
 * The army's own units (A61, K1): every unit of the kingdom but a soldier seat's, held or not.
 * What the army's room, the levy's price and a city's column count: a seat is the account's,
 * and every kingdom of a mode has the same seats, so the exemption is fair.
 */
export function armyUnitsOf(state: RunState, faction: FactionId): Unit[] {
  const units = unitsOf(state, faction);
  const seated = state.seats.filter((seat) => seat.unitId !== undefined);
  return seated.length === 0
    ? units
    : units.filter((unit) => !seated.some((seat) => seat.unitId === unit.id));
}

/**
 * The kingdoms a human sits in (K1): every one with a held seat, in seat order. Such a kingdom
 * never surrenders by rule (B3.3): giving up is a human's choice, not a script's.
 */
export function heldFactions(state: RunState): FactionId[] {
  const factions: FactionId[] = [];
  for (const seat of state.seats) {
    if (seat.held && !factions.includes(seat.faction)) {
      factions.push(seat.faction);
    }
  }
  return factions;
}
